// Client-side helpers for enabling/disabling push notifications.
import { saveSubscription, removeSubscription } from "@/lib/push.functions";

// Public VAPID key (safe to ship to the browser).
export const VAPID_PUBLIC_KEY =
  "BJTwhDRIEteFEqZd9_84bbZgtZhjeB4XBlibPVN8J13Pi1vn6Bx4YGkhr4l5uOSSjtqAvpuYOkazOzJpMq3lHi8";

export type PushTopic = "orders" | "offers" | "new_products";

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function urlBase64ToUint8Array(base64: string) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// Resolves to an active service worker registration, registering /sw.js if
// needed. `navigator.serviceWorker.ready` can hang forever on a fresh visit
// (especially on mobile) if the worker hasn't activated yet, so we register
// explicitly and race `ready` against a timeout before falling back to the
// existing registration.
async function getReadyRegistration(): Promise<ServiceWorkerRegistration> {
  // Make sure a registration exists; if it's already there this resolves fast.
  let reg =
    (await navigator.serviceWorker.getRegistration()) ??
    (await navigator.serviceWorker.register("/sw.js"));

  if (reg.active) return reg;

  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 10000));
  const ready = await Promise.race([navigator.serviceWorker.ready, timeout]);
  if (ready) return ready;

  // Timed out waiting for activation: use whatever registration we have.
  reg = (await navigator.serviceWorker.getRegistration()) ?? reg;
  if (!reg.active && !reg.installing && !reg.waiting) {
    throw new Error("Ο browser δεν μπόρεσε να ενεργοποιήσει τις ειδοποιήσεις. Δοκίμασε ξανά.");
  }
  return reg;
}

export async function getPushStatus(): Promise<"unsupported" | "denied" | "subscribed" | "default"> {
  if (!pushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  const reg = Notification.permission === "granted"
    ? await navigator.serviceWorker.ready
    : await navigator.serviceWorker.getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return sub ? "subscribed" : "default";
}

export async function enablePush(
  topics: PushTopic[] = ["orders", "offers", "new_products"],
) {
  if (!pushSupported()) throw new Error("Ο browser δεν υποστηρίζει ειδοποιήσεις.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Δεν δόθηκε άδεια για ειδοποιήσεις.");

  const reg = await getReadyRegistration();
  const sub =
    (await reg.pushManager.getSubscription()) ||
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    }));

  const json = sub.toJSON();
  await saveSubscription({
    data: {
      endpoint: json.endpoint!,
      p256dh: json.keys!.p256dh!,
      auth: json.keys!.auth!,
      topics,
    },
  });
}

export async function disablePush() {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (sub) {
    await removeSubscription({ data: { endpoint: sub.endpoint } }).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
}

// Re-saves an already-granted subscription to the database without prompting.
// Recovers cases where the browser subscription exists but the server-side
// save failed (e.g. a stale schema cache). Safe to call on every page load:
// it only acts when a subscription already exists and the upsert is keyed by
// endpoint, so it never creates duplicates.
export async function syncSubscription(
  topics: PushTopic[] = ["orders", "offers", "new_products"],
) {
  if (!pushSupported()) return false;
  if (Notification.permission !== "granted") return false;
  const reg = await getReadyRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return false;
  await saveSubscription({
    data: {
      endpoint: json.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
      topics,
    },
  });
  return true;
}
